import React, { useState, useEffect, useRef } from 'react';
import { TopBar } from './TopBar';
import { LeftSidebar } from './LeftSidebar';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/features/auth/AuthProvider';
import { isActiveStudent } from '@/utils/roleChecks';
import { OnboardingWizard } from '@/features/onboarding/OnboardingWizard';
import { SubscriptionLockedOverlay } from '@/features/subscriptions/SubscriptionLockedOverlay';
import { RejectedAccessScreen } from '@/features/auth/RejectedAccessScreen';
import { MobileBottomMenu } from './MobileBottomMenu';
import { ImpersonationBar } from './ImpersonationBar';
import { PullToRefresh } from '@/components/ui/pull-to-refresh';

interface DashboardLayoutProps {
    children: React.ReactNode;
    subscriptionState?: 'active' | 'grace_period' | 'locked';
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, subscriptionState = 'active' }) => {
    const { currentUser, isImpersonating } = useAuth();
    const { i18n } = useTranslation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();
    const mainRef = useRef<HTMLDivElement>(null);
    const isRtl = i18n.dir() === 'rtl';

    // RightSidebar is now handled within Dashboard.tsx itself to improve unmounting stability

    // Check if user is pending approval
    const isPending = isActiveStudent(currentUser) && currentUser?.status === 'pending';

    // Scroll to top on route change
    useEffect(() => {
        if (mainRef.current) {
            mainRef.current.scrollTop = 0;
            window.scrollTo(0, 0); // Also scroll window just in case
        }
    }, [location.pathname]);

    // Check if locked
    const isLocked = subscriptionState === 'locked';

    return (
        <div className="min-h-screen bg-[#F0F2F5] relative overflow-x-hidden">
            {/* Fixed TopBar - Always at the very top of the browser */}
            <TopBar onMenuClick={() => setMobileMenuOpen(true)} />

            {/* Impersonation Banner - Fixed below TopBar */}
            <ImpersonationBar />

            {/* Grace Period Banner */}
            {subscriptionState === 'grace_period' && (
                <div className="bg-red-600 text-white text-center py-2 px-4 text-sm font-medium animate-in slide-in-from-top fixed top-16 left-0 right-0 z-[60]">
                    Your subscription has expired. Please renew within 2 days to avoid account lockout.
                </div>
            )}

            {/* Mobile Menu Sheet */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetContent side={isRtl ? 'right' : 'left'} className="p-0 w-[240px]">
                    <LeftSidebar
                        className="h-full"
                        onItemClick={() => setMobileMenuOpen(false)}
                    />
                </SheetContent>
            </Sheet>

            {/* If pending or locked, blur the main content area */}
            <div className={`transition-all duration-300 ${isPending || isLocked ? 'filter blur-sm pointer-events-none select-none h-screen overflow-hidden' : ''}`}>
                <div className="flex min-h-screen pt-16">
                    {/* Left Sidebar (Desktop) - Fixed, starting below TopBar */}
                    <div className={`hidden lg:block w-[240px] fixed start-0 bottom-0 overflow-y-auto custom-scrollbar hover:overflow-y-auto ${isImpersonating ? 'top-[104px]' : 'top-16'}`}>
                        <LeftSidebar className="h-full" />
                    </div>

                    {/* Main Feed Area */}

                    <main
                        ref={mainRef}
                        className={`flex-1 lg:ms-[240px] px-2 md:px-6 pb-20 md:pb-6 pt-4 mx-auto w-full overflow-x-hidden`}
                    >
                        <PullToRefresh>
                            {children}
                        </PullToRefresh>
                    </main>

                    {/* RightSidebar is now rendered inside Dashboard.tsx */}
                </div>

                {/* Mobile Bottom Menu */}
                <MobileBottomMenu />
            </div>

            {/* Overlay for Pending Users */}
            {isPending && (
                <div className="fixed inset-0 z-[100] overflow-y-auto">
                    <OnboardingWizard variant="overlay" />
                </div>
            )}

            {/* Overlay for Locked Subscriptions - Reusing style */}
            {isLocked && !isPending && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <SubscriptionLockedOverlay />
                </div>
            )}

            {/* Overlay for Rejected Users */}
            {currentUser?.status === 'rejected' && (
                <RejectedAccessScreen />
            )}
        </div>
    );
};
